import { useEffect, useMemo, useState } from "react";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { IndividualPaymentDue } from "../payment-dues.entity";
import PaymentDueActionsDropdown from "@/app/vendors/payment-dues/components/PaymentDueActionsDropdown";

interface IndividualPaymentDuesTableProps {
  data: IndividualPaymentDue[];
  loading?: boolean;
  onPayViaStripe?: (due: IndividualPaymentDue) => Promise<void> | void;
  onMarkAsPaid?: (due: IndividualPaymentDue) => Promise<void> | void;
  onDownloadReport?: (due: IndividualPaymentDue) => Promise<void> | void;
  markingKey?: string | null;
  downloadingKey?: string | null;
  stripePayingKey?: string | null;
}

export default function IndividualPaymentDuesTable({
  data,
  loading,
  onPayViaStripe,
  onMarkAsPaid,
  onDownloadReport,
  markingKey,
  downloadingKey,
  stripePayingKey,
}: IndividualPaymentDuesTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [data.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((data.length || 1) / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [data.length, pageSize, page]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  const columns = useMemo<ColumnDef<IndividualPaymentDue>[]>(
    () => [
      {
        header: "Individual Name",
        accessor: (row) => <span className="font-medium">{row.name}</span>,
      },
      {
        header: "Remaining Amount (All added)",
        accessor: (row) => `$${row.remaining_amount.toFixed(2)}`,
        className: "justify-end",
        cellClassName: "text-right font-semibold",
      },
      {
        header: "No. of Appointments (all payment remaining)",
        accessor: (row) => row.appointment_count,
        className: "justify-end",
        cellClassName: "text-right",
      },
      {
        header: "No. of Daily Work (all remaining)",
        accessor: (row) => row.daily_work_count,
        className: "justify-end",
        cellClassName: "text-right",
      },
      {
        header: "Actions",
        accessor: (row) => (
          <PaymentDueActionsDropdown
            onPayViaStripe={onPayViaStripe ? () => onPayViaStripe(row) : undefined}
            onMarkAsPaid={onMarkAsPaid ? () => onMarkAsPaid(row) : undefined}
            onDownloadReport={onDownloadReport ? () => onDownloadReport(row) : undefined}
            markingPaid={markingKey === `individual:${row.id}`}
            downloading={downloadingKey === `individual:${row.id}`}
            payingViaStripe={stripePayingKey === `individual:${row.id}`}
          />
        ),
        className: "justify-end",
        cellClassName: "text-right",
      },
    ],
    [downloadingKey, markingKey, onDownloadReport, onMarkAsPaid, onPayViaStripe, stripePayingKey]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <SimpleTable<IndividualPaymentDue>
      data={paginatedData}
      columns={columns}
      totalCount={data.length}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      filters={[]}
      onReset={() => {}}
      container={false}
    />
  );
}
